// Toast copy for stage-automation API errors (CORE-4897 category 6).
//
// These are USER-ACTION failures, not run failures: saving an automation,
// cancelling or resuming an execution, a bulk trigger. They come back as an
// `ApiError` whose `errors[0]` is a raw code, and until now the drawer passed
// that straight to `resolveErrorMessage`, which surfaced the raw string —
// recruiters were seeing `err_stage_automation_exists` in a toast.
//
// Distinct from `automationErrorCatalogue.ts`, which covers what happened to a
// RUN (feed chip + tooltip). Nothing here ever renders in the activities feed.
//
// Codes verified against the `core` backend (Aug 2026):
// `workflow_executions_controller.rb`, `workflow_rules_controller.rb`, and the
// two contracts under `app/contracts/api_ats/v1/`.

import { ApiError } from '@/api/request';

export const AUTOMATION_API_ERROR_COPY: Record<string, string> = {
  // --- Execution lookup / state ---
  err_execution_not_found:
    'This automation run no longer exists. Refresh the page.',
  err_not_found: 'This item no longer exists. Refresh the page.',
  err_execution_not_pending:
    'This automation is no longer scheduled. Refresh to see its current status.',
  err_execution_not_cancellable:
    'This automation can’t be canceled because it already started',
  err_execution_not_resumable:
    'This automation can’t be resumed from its current status',
  err_execution_not_active:
    'This automation is no longer running. Refresh to see its current status.',
  err_action_already_started:
    'This action already started and can’t be run again',
  err_invalid_position:
    'That step isn’t part of this automation. Refresh the page and try again.',

  // --- Bulk request shape (controller-level) ---
  err_filter_required: 'Bulk actions need a filter. Apply one and try again.',
  err_subject_ids_or_session_required:
    'No candidates selected. Select candidates and try again.',
  err_ids_too_many:
    'Too many automation runs selected. Select fewer and try again.',
  err_subject_ids_too_many:
    'Too many candidates selected. Select fewer and try again.',
  err_invalid_execution_ids:
    'Some automation runs in this selection are no longer valid. Refresh the page and try again.',
  // PROVISIONAL — the same endpoints also validate at the CONTRACT layer, which
  // raises its own codes. Not in Product's sheet.
  err_execution_ids_empty:
    'No automation runs selected. Select at least one and try again.',
  err_execution_ids_too_many:
    'Too many automation runs selected. Select fewer and try again.',

  // --- Rule save / delete ---
  err_stage_automation_exists:
    'This stage already has an automation. Edit the existing one instead.',
  err_stage_not_manageable: 'This stage can’t be automated',
  err_actions_empty: 'No activities added. Add at least one activity to save.',
  err_activity_not_found_or_deleted:
    'Some activities in this automation were deleted. Remove them and try again.',
  err_unsupported_activity_kind:
    'Some activities can’t be automated. Remove them and try again.',
  err_missing_ats_job_stage_action_id:
    'This automation is missing an activity. Add one and try again.',
  // The rule-save path also rejects an activity left without participants.
  // The backend appends the activity's free-text label, which we drop — the
  // label is user-authored and reads poorly mid-sentence.
  err_activity_participants_missing:
    'An activity in this automation has no participants. Add at least one and try again.',
  // PROVISIONAL — `workflow_rules_contract.rb` validations. Not in Product's sheet.
  err_duplicate_positions:
    'Two activities are in the same position. Reorder them and try again.',
  err_too_many_actions:
    'This automation has too many activities. Remove some and try again.',
  err_unknown_action_type:
    'This automation contains an activity we don’t recognise. Remove it and try again.',
  err_unsupported_event: 'This automation’s trigger isn’t supported',
  err_scope_trigger_mismatch:
    'This automation’s trigger doesn’t match the stage it’s on. Reopen it and save again.',
};

const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

// The BE sends some codes as `code: detail` (`err_unsupported_activity_kind:
// interview,review`). The detail names internal ids/kinds, so we key off the
// code and drop it.
const codeOf = (raw: string): string => raw.split(':')[0].trim();

/**
 * Toast copy for a failed stage-automation API call. Follows the pattern
 * `resolveErrorMessage` recommends: consult the feature's own copy map first,
 * and only then fall back — so a raw `err_*` code is never shown.
 */
export const getAutomationApiErrorCopy = (
  err: unknown,
  fallback: string = DEFAULT_FALLBACK
): string => {
  if (err instanceof ApiError) {
    for (const raw of err.errors) {
      const copy = AUTOMATION_API_ERROR_COPY[codeOf(raw)];
      if (copy) return copy;
    }
    // An unmapped `err_*` code is a raw token — prefer the caller's fallback
    // over leaking it. A BE message with spaces is already human, so show it.
    const human = err.errors.find(
      (e) => e.includes(' ') && !/^err_[a-z0-9_]+/.test(e)
    );
    if (human) return human;
  }
  return fallback;
};
